import { array, arrayOf, bool, func, number, oneOfType, shape, string } from 'prop-types';
import React, { useCallback, useContext, useEffect } from 'react';
import { v4 as uuid } from 'uuid';
import { StandardButton } from '@eika/button';
import AHVContext from '../../../ahv-context';
import { emptyList } from '../../../constants';
import getLabel from '../labels';
import ReellRettighetshaver, { ekstrateksterShape, rrConfigShape } from './reell-rettighetshaver';
import {
    answersToBeneficialOwner,
    getReelleRettighetshaverFields,
    getSkatterelasjonFields,
    isValid,
} from './validation';
import CountryContext from '../../../country-context';

export const calculateTotalOwnershipShares = (subQuestions) => {
    if (!subQuestions) return 0;
    return subQuestions
        .map(({ eierandelProsent }) => parseFloat(eierandelProsent))
        .reduce((accumulator, ownership) => accumulator + ownership);
};

const RrQuestion = ({
    question,
    subquestions,
    onChange,
    getHasSubmitted,
    appendAnswer,
    getTotalOwnershipShares,
    appNamespace,
}) => {
    useEffect(() => {
        if (subquestions.length === 0) {
            appendAnswer(subquestions);
        }
    }, [appendAnswer, subquestions]);

    const makeRemoveSelf = useCallback(
        (rrId) => () => {
            const newState = subquestions.filter((el) => el.rrId !== rrId);
            onChange(question.definisjon, { value: newState });
        },
        [onChange, question.definisjon, subquestions]
    );
    const countryContext = useContext(CountryContext);

    const getShowErrors = useCallback(() => getHasSubmitted(), [getHasSubmitted]);

    const getInvalidList = useCallback(
        (newAns, config) => {
            const keys = Object.keys(getReelleRettighetshaverFields());
            if (getTotalOwnershipShares() > 100) {
                keys.push('eierandelProsentCumulated');
            }
            const beneficialOwner = answersToBeneficialOwner(newAns);
            return keys.filter((key) =>
                isValid(key, beneficialOwner, config, countryContext.getNorwegianPostalCodesOptions) ? false : key
            );
        },
        [getTotalOwnershipShares, countryContext.getNorwegianPostalCodesOptions]
    );

    const makeUpdateAnswer = useCallback(
        (rrId) => (patch) => {
            const newState = subquestions.map((el) => {
                const newAns = { ...el, ...patch };
                const hasError = getShowErrors() ? getInvalidList(newAns).length > 0 : false;
                return el.rrId === rrId ? { ...newAns, error: hasError } : el;
            });
            onChange(question.definisjon, { value: newState });
        },
        [onChange, question.definisjon, subquestions, getShowErrors, getInvalidList]
    );

    const handleClick = useCallback(() => appendAnswer(subquestions), [appendAnswer, subquestions]);

    return (
        <div className="rr-skjema">
            <div className="rr-underskjema">
                {subquestions.map((subquestion, i, arr) => (
                    <ReellRettighetshaver
                        key={`answer_${subquestion.rrId}`}
                        index={i}
                        listLength={arr.length}
                        question={subquestion}
                        config={question.config}
                        getHasSubmitted={getHasSubmitted}
                        onChange={makeUpdateAnswer(subquestion.rrId)}
                        onRemove={makeRemoveSelf(subquestion.rrId)}
                        instruks={question.instruks}
                        ekstratekster={question.ekstratekster}
                        showErrors={getShowErrors()}
                        errors={getShowErrors() ? getInvalidList(subquestion, question.config) : emptyList}
                        appNamespace={appNamespace}
                    />
                ))}
            </div>
            <div className="skjema-input-field-wrap-small skjema-input-field-wrap">
                <label htmlFor="rr-add">{getLabel(question.ekstratekster, 'rr_flere_label')}</label>
                <StandardButton id="rr-add" onClick={handleClick}>
                    <i className="ikon-pluss-24" aria-hidden />
                    {getLabel(question.ekstratekster, 'rr_flere')}
                </StandardButton>
            </div>
        </div>
    );
};

RrQuestion.propTypes = {
    subquestions: arrayOf(
        shape({
            error: bool,
            touched: bool,
            navn: string,
            fodeland: string,
            statsborgerskap: string,
            sekundartStatsborgerskap: string,
            fnr: string,
            adresse: string,
            postnummer: string,
            poststed: string,
            bostedsland: string,
            epost: string,
            mobil: string,
            eierandelProsent: oneOfType([string, number]),
            bestemmendeKontroll: bool,
            bestemmendeKontrollType: string,
            pep: bool,
            pepVerv: string,
            pepRelasjon: string,
            skatterelasjonUsa: bool,
            skatterelasjonUsaTin: string,
            skatterelasjonUsaTinMangler: string,
            skatterelasjonerFlere: bool,
            skatterelasjoner: array,
        })
    ),
    onChange: func,
    getHasSubmitted: func,
    appendAnswer: func,
    getTotalOwnershipShares: func,
};

const RrQuestionType = ({ question, appNamespace }) => {
    const { getValue, onChange, getHasSubmitted } = useContext(AHVContext);

    const getSubquestions = useCallback(() => {
        let subquestions = getValue(question.definisjon);
        if (typeof subquestions === 'string') {
            subquestions = JSON.parse(subquestions).map((subq) => ({
                rrId: uuid(),
                ...getReelleRettighetshaverFields(),
                ...subq,
                fnr: subq.aktorId ? subq.aktorId.value : null,
                skatterelasjoner: subq.skatterelasjoner ? [...subq.skatterelasjoner] : [getSkatterelasjonFields()],
            }));

            onChange(question.definisjon, { value: subquestions });
        }
        return subquestions;
    }, [onChange, getValue, question.definisjon]);

    const getTotalOwnershipShares = useCallback(
        () => calculateTotalOwnershipShares(getSubquestions()),
        [getSubquestions]
    );

    const appendAnswer = useCallback(
        (oldVal) => {
            const newState = [
                ...oldVal,
                {
                    rrId: uuid(),
                    ...getReelleRettighetshaverFields(),
                },
            ];

            onChange(question.definisjon, { value: newState });
        },
        [onChange, question.definisjon]
    );

    return (
        <RrQuestion
            onChange={onChange}
            getHasSubmitted={getHasSubmitted}
            subquestions={getSubquestions()}
            question={question}
            getTotalOwnershipShares={getTotalOwnershipShares}
            appendAnswer={appendAnswer}
            appNamespace={appNamespace}
        />
    );
};

RrQuestionType.propTypes = {
    appNamespace: string,
    question: shape({
        config: rrConfigShape,
        definisjon: string.isRequired,
        eksternId: string.isRequired,
        ekstratekster: ekstrateksterShape,
        instruks: string,
        muligeSvar: array,
        sporsmalstekst: string,
        synlighet: string,
        type: string,
        utfyllingsregel: shape({
            regel: string,
        }),
    }),
};

export default RrQuestionType;
